import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO_WORDMARK } from "@/lib/data";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/#services" },
  { label: "Team", to: "/team" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
    }
  };

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-[100] transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled ? "bg-black/60 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <img src={LOGO_WORDMARK} alt="DVG Sports" className="h-9 w-auto" />
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {LINKS.map((l) => (
              <button
                key={l.label}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                onClick={() => go(l.to)}
                className="text-[13px] uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-[#cbd1d8] hover:after:w-full after:transition-[width] after:duration-300"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-contact-btn"
              onClick={() => go("/#contact")}
              className="bg-[#cbd1d8] text-black text-[13px] font-bold uppercase tracking-[0.14em] px-6 py-3 hover:bg-white transition-colors duration-300"
            >
              Work With Us
            </button>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="fixed inset-0 z-[99] bg-black/95 backdrop-blur-xl pt-24 px-8 md:hidden"
          >
            <div className="flex flex-col gap-6">
              {[...LINKS, { label: "Work With Us", to: "/#contact" }].map((l) => (
                <button
                  key={l.label}
                  data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => go(l.to)}
                  className="font-display text-5xl text-left text-white hover:text-[#cbd1d8] transition-colors"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
